import { useEffect, useRef, useState } from 'react';
import { Modal } from '@mui/material';
import { Close, EditOutlined, PauseRounded, PlayArrowRounded } from '@mui/icons-material';
import { cancelPendingAudioSeek } from './audioHelper';
import { fancyTimeFormat } from './timeHelper';
import './NoteTimestampEditor.css';

const parseTime = text => {
    let value = text.trim();
    if (/^\d{1,7}$/.test(value)) {
        const digits = value.padStart(4, '0');
        value = digits.length > 4
            ? `${Number(digits.slice(0, -4))}:${digits.slice(-4, -2)}:${digits.slice(-2)}`
            : `${Number(digits.slice(0, -2))}:${digits.slice(-2)}`;
    }
    if (!/^\d{1,3}:\d{2}(:\d{2})?$/.test(value)) return null;
    const parts = value.split(':').map(Number);
    if (parts.at(-1) > 59 || (parts.length === 3 && parts[1] > 59)) return null;
    return parts.reduce((total, part) => total * 60 + part, 0);
};
const finiteDuration = value => Number.isFinite(value) && value > 0 ? Math.floor(value) : Infinity;

// Mounted only while the composer is editing its timestamp. The attachment is
// deliberately left untouched until Save; media preview shares the existing player.
const NoteTimestampEditor = ({ initialSeconds, durationSeconds, audioSource, onSave, onClose }) => {
    const [draft, setDraft] = useState(() => Math.min(finiteDuration(durationSeconds), Math.max(0, Math.floor(initialSeconds || 0))));
    const [text, setText] = useState(() => fancyTimeFormat(draft));
    const [maximum, setMaximum] = useState(() => finiteDuration(durationSeconds));
    const [playing, setPlaying] = useState(false);
    const [available, setAvailable] = useState(false);
    const [error, setError] = useState('');
    const inputRef = useRef(null);
    const draftRef = useRef(draft);
    const editing = useRef(false);
    const previewActive = useRef(false);
    const playRequest = useRef(0);
    const pendingStart = useRef(null);
    const dragStart = useRef(null);
    // Metadata updates change the bounds without restarting the preview session.
    const episodeDuration = useRef(durationSeconds);
    episodeDuration.current = durationSeconds;
    const audio = document.getElementById('audio');
    const source = audioSource ? new URL(audioSource, document.baseURI).href : null;
    const matches = () => !!audio && !!source && audio.src === source;
    const parsed = parseTime(text);
    const valid = parsed !== null && parsed <= maximum;

    const showTime = seconds => {
        const value = Math.max(0, Math.floor(seconds));
        draftRef.current = value;
        setDraft(value);
        setText(fancyTimeFormat(value));
    };

    const seek = seconds => {
        if (!matches()) return;
        cancelPendingAudioSeek(audio);
        pendingStart.current = audio.readyState < 1 ? seconds : null;
        try { audio.currentTime = seconds; } catch { pendingStart.current = seconds; }
    };

    const pausePreview = () => {
        playRequest.current += 1;
        previewActive.current = false;
        if (matches()) audio.pause();
        setPlaying(false);
    };

    useEffect(() => {
        if (!audio || !source) return undefined;
        let active = true;
        const sameSource = () => audio.src === source;
        const updatePlayback = () => {
            if (!active) return;
            const same = sameSource();
            setAvailable(same);
            setPlaying(same && !audio.paused && !audio.ended);
            if (!same) {
                playRequest.current += 1;
                previewActive.current = false;
                pendingStart.current = null;
            } else if (previewActive.current && !editing.current &&
                pendingStart.current === null && !audio.seeking && Number.isFinite(audio.currentTime)) {
                const limit = Math.min(finiteDuration(episodeDuration.current), finiteDuration(audio.duration));
                showTime(Math.min(audio.currentTime, limit));
                if (audio.currentTime >= limit && !audio.paused) audio.pause();
            }
        };
        const updateDuration = () => {
            if (!sameSource()) { updatePlayback(); return; }
            const limit = Math.min(finiteDuration(episodeDuration.current), finiteDuration(audio.duration));
            setMaximum(limit);
            if (!editing.current && draftRef.current > limit) showTime(limit);
            if (pendingStart.current !== null && audio.readyState >= 1) {
                const target = Math.min(limit, pendingStart.current);
                pendingStart.current = null;
                audio.currentTime = target;
            }
            updatePlayback();
        };
        const onError = () => {
            if (sameSource()) {
                setPlaying(false);
                setError('Couldn’t play this audio. Try again.');
            }
        };
        const playbackEvents = ['timeupdate', 'play', 'pause', 'ended', 'seeked', 'emptied'];
        const durationEvents = ['loadedmetadata', 'loadeddata', 'durationchange'];
        playbackEvents.forEach(event => audio.addEventListener(event, updatePlayback));
        durationEvents.forEach(event => audio.addEventListener(event, updateDuration));
        audio.addEventListener('error', onError);
        if (sameSource()) {
            audio.pause();
            cancelPendingAudioSeek(audio);
            const limit = Math.min(finiteDuration(episodeDuration.current), finiteDuration(audio.duration));
            const value = Math.min(draftRef.current, limit);
            setMaximum(limit);
            showTime(value);
            pendingStart.current = audio.readyState < 1 ? value : null;
            try { audio.currentTime = value; } catch { pendingStart.current = value; }
        }
        updatePlayback();
        return () => {
            active = false;
            playRequest.current += 1;
            previewActive.current = false;
            playbackEvents.forEach(event => audio.removeEventListener(event, updatePlayback));
            durationEvents.forEach(event => audio.removeEventListener(event, updateDuration));
            audio.removeEventListener('error', onError);
            if (sameSource()) audio.pause();
        };
    }, [audio, source]);

    useEffect(() => {
        const limit = Math.min(finiteDuration(durationSeconds),
            audio && audio.src === source ? finiteDuration(audio.duration) : Infinity);
        setMaximum(limit);
        if (!editing.current && draftRef.current > limit) showTime(limit);
    }, [audio, source, durationSeconds]);

    const adjust = delta => {
        const value = Math.min(maximum, Math.max(0, draftRef.current + delta));
        editing.current = false;
        setError('');
        showTime(value);
        seek(value);
    };

    const togglePlayback = async () => {
        if (!matches() || !valid) return;
        if (!audio.paused) { pausePreview(); return; }
        previewActive.current = true;
        editing.current = false;
        setError('');
        seek(draftRef.current);
        const request = ++playRequest.current;
        try { await audio.play(); } catch {
            if (request === playRequest.current && matches()) {
                previewActive.current = false;
                setPlaying(false);
                setError('Couldn’t play this audio. Try again.');
            }
        }
    };

    const dismiss = () => { pausePreview(); onClose(); };
    const finishEditing = () => {
        if (!valid) {
            setError(Number.isFinite(maximum)
                ? `Enter a time from 0:00 to ${fancyTimeFormat(maximum)}.`
                : 'Enter a time as m:ss or h:mm:ss.');
            return;
        }
        editing.current = false;
        showTime(parsed);
        seek(parsed);
        setError('');
    };

    return <Modal open onClose={dismiss} className='noteTimestampModal'>
        <div className='noteTimestampEditor' role='dialog' aria-modal='true' aria-label='Save note time'>
            <div className='noteTimestampTop' onPointerDown={event => {
                if (event.target.closest('button')) return;
                dragStart.current = { x: event.clientX, y: event.clientY };
                event.currentTarget.setPointerCapture(event.pointerId);
            }} onPointerUp={event => {
                const start = dragStart.current;
                dragStart.current = null;
                if (start && event.clientY - start.y > 50 && Math.abs(event.clientX - start.x) < 80) dismiss();
            }} onPointerCancel={() => { dragStart.current = null; }}>
                <div className='noteTimestampHandle' aria-hidden='true' />
                <button type='button' className='noteTimestampClose' aria-label='Close without saving' onClick={dismiss} autoFocus><Close /></button>
            </div>
            <div className={`noteTimestampClock${text.length > 5 ? ' noteTimestampClockLong' : ''}`}>
                <button type='button' className='noteTimestampPlay' aria-label={playing ? 'Pause' : 'Play'}
                    aria-pressed={playing} disabled={!available || !valid || (!playing && draft >= maximum)}
                    title={!available ? 'Open this episode to preview audio' : undefined} onClick={togglePlayback}>
                    {playing ? <PauseRounded /> : <PlayArrowRounded />}
                </button>
                <input ref={inputRef} type='text' inputMode='numeric' autoComplete='off' spellCheck={false}
                    aria-label='Note time' aria-invalid={!valid} aria-describedby={error ? 'noteTimestampError' : undefined}
                    value={text} maxLength={9} style={{ width: `${Math.max(4.3, Math.min(9, text.length + .3))}ch` }}
                    onFocus={event => { editing.current = true; pausePreview(); event.target.select(); }}
                    onChange={event => {
                        const next = event.target.value;
                        setText(next);
                        setError('');
                        const seconds = parseTime(next);
                        if (seconds !== null && seconds <= maximum) { draftRef.current = seconds; setDraft(seconds); }
                    }} onBlur={finishEditing} onKeyDown={event => {
                        if (event.key === 'Enter') { event.preventDefault(); inputRef.current.blur(); }
                    }} />
                <button type='button' className='noteTimestampEdit' aria-label='Edit note time'
                    onClick={() => inputRef.current.focus()}><EditOutlined /></button>
            </div>
            {error && <p className='noteTimestampError' id='noteTimestampError' role='alert'>{error}</p>}
            <div className='noteTimestampSteps' aria-label='Adjust note time'>
                {[1, 5, 30].map(step => <div className='noteTimestampPair' role='group' aria-label={`${step} second adjustments`} key={step}>
                    <span>{step}s</span>
                    {[-1, 1].map(direction => <button type='button' key={direction}
                        aria-label={`${step} ${step === 1 ? 'second' : 'seconds'} ${direction < 0 ? 'earlier' : 'later'}`}
                        disabled={!valid || draft + direction * step < 0 || draft + direction * step > maximum}
                        onClick={() => adjust(direction * step)}>{direction < 0 ? '−' : '+'}</button>)}
                </div>)}
            </div>
            <button type='button' className='noteTimestampSave' disabled={!valid} onClick={() => {
                const value = draftRef.current;
                pausePreview();
                onSave(value);
            }}>Save note time</button>
        </div>
    </Modal>;
};

export default NoteTimestampEditor;
